import type {AttachmentCollectionFragment} from "app/gql/graphqlSchema";
import type {FileToShare, MaybeNil} from "app/utils/types";
import {IonActionSheet} from "@ionic/react";
import {forwardRef, useCallback, useImperativeHandle, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {closeOutline, cloudDownloadOutline, shareSocialOutline} from "ionicons/icons";
import type {ActionSheetButton} from "@ionic/core/components";
import {useBoolean} from "@fluentui/react-hooks";
import {useShareAction} from "app/employee/hooks/useShareAction";

interface Props {
  attachmentCollection: AttachmentCollectionFragment;
  name: MaybeNil<string>;
}

export interface ShareDialogHandle {
  showShare: (attachmentIndex?: number) => void;
}

export const ShareDialog = forwardRef<ShareDialogHandle, Props>(({attachmentCollection, name}, ref) => {
  const {t} = useTranslation();

  const shareAttach = useShareAction();

  const shareDownload = useCallback(async (file: FileToShare) => {
    window.open(file.url, "_blank");
  }, []);

  const buttons = useMemo<ActionSheetButton[]>(() => {
    const pdfFileToShare = {
      url: attachmentCollection.pdfUrl,
      filename: `${name}.pdf`,
      humanName: name
    } satisfies FileToShare;
    return [
      {
        text: t("g.share.pdf-file-attach"),
        icon: shareSocialOutline,
        handler: async () => {
          await shareAttach(pdfFileToShare);
        }
      },
      {
        text: t("g.share.pdf-file-download"),
        icon: cloudDownloadOutline,
        handler: async () => {
          await shareDownload(pdfFileToShare);
        }
      },
      {
        text: t("g.close"),
        icon: closeOutline,
        role: "cancel"
      }
    ];
  }, [attachmentCollection, name, shareAttach, shareDownload, t]);

  const [isOpen, {setTrue: openActionSheet, setFalse: closeActionSheet}] = useBoolean(false);

  useImperativeHandle(
    ref,
    () => ({
      showShare: openActionSheet
    }),
    [openActionSheet]
  );

  return (
    <IonActionSheet
      buttons={buttons}
      header={t("g.share.title", {defaultValue: "Споделяне на документ {{name}}", name})}
      isOpen={isOpen}
      onDidDismiss={closeActionSheet}
    />
  );
});

ShareDialog.displayName = "ShareDialog";
